import { db } from '@core/db/db'
import { META_DEVICE_TOKEN } from '@core/db/schema'
import { isNativeKkt } from '@core/native/kkt'
import { syncService } from '@core/services/sync/SyncService'
import { fiscalService } from './FiscalService'
import { debugLog } from '@core/utils/log'

/**
 * Печать чужих offline-чеков зоны на ККТ этого POS.
 *
 * Телефон не имеет ККТ: его offline_pending уходит в облачную очередь.
 * Этот воркер забирает чужой чек, печатает на native-ККТ и возвращает в
 * облако фискальные реквизиты. Локальные чеки этого POS обрабатывает
 * обычный FiscalQueueWorker, поэтому claim исключает свой device_id.
 */
export class CloudFiscalizationWorker {
  private intervalId: number | null = null
  private running = false

  public async processPending(): Promise<number> {
    // Только устройство с native-ККТ может быть фискальным узлом.
    if (!isNativeKkt() || this.running) return 0
    if (fiscalService.getKktStatus()?.state !== 'ok') return 0
    if (!navigator.onLine) return 0

    const tokenEntry = await db.meta.get(META_DEVICE_TOKEN)
    if (!tokenEntry || typeof tokenEntry.value !== 'string' || !tokenEntry.value) return 0

    this.running = true
    try {
      const deviceId = await syncService.ensureDeviceId()
      const zoneId = await syncService.ensureZoneId()
      const item = await syncService.claimFiscalization(zoneId, deviceId)
      if (!item) return 0

      try {
        const result = await fiscalService.printFiscalReceipt({
          id: item.receiptId,
          cashierName: item.cashierName,
          total_amount: Math.abs(item.totalAmount),
          paymentMethod: item.paymentMethod,
          items: item.items.map((row) => ({
            name: row.name,
            price: row.price,
            quantity: row.quantity,
            unit: row.unit,
            marking_code: row.marking_code
          })),
          docType: item.docType,
          settlementType: item.settlementType
        })
        await syncService.completeFiscalization(item.queueId, deviceId, {
          fiscalDocumentNumber: result.fiscalDocumentNumber,
          fiscalDocumentSign: result.fiscalDocumentSign,
          shiftNumber: result.shiftNumber,
          fiscalDriveNumber: result.fiscalDriveNumber,
          receiptNumber: result.receiptNumber,
          dateTime: result.dateTime
        })
        debugLog(`[CloudFiscalization] чек ${item.receiptId} дофискализирован`)
        return 1
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        await syncService
          .failFiscalization(item.queueId, deviceId, message)
          .catch((ackError) =>
            console.warn('[CloudFiscalization] не удалось вернуть ошибку:', ackError)
          )
        console.warn('[CloudFiscalization] печать не удалась:', message)
        return 0
      }
    } finally {
      this.running = false
    }
  }

  public start(intervalMs = 60_000): void {
    if (this.intervalId != null) return
    void this.processPending()
    this.intervalId = window.setInterval(() => {
      void this.processPending()
    }, intervalMs)
  }

  public stop(): void {
    if (this.intervalId != null) {
      window.clearInterval(this.intervalId)
      this.intervalId = null
    }
  }
}

export const cloudFiscalizationWorker = new CloudFiscalizationWorker()
